import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Users, Building2, Search } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const PerformanceTypes = () => {
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchTypes();
  }, [filialeId, isConsolidated]);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      
      const [typesRes, personnelRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/personnel').catch(() => ({ data: [] }))
      ]);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];

      // Si contexte filiale spécifique, filtrer les types qui ont du personnel dans cette filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        const personnelInFiliale = allPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
        const typeIdsInFiliale = [...new Set(personnelInFiliale.map(p => p.type_personnel_id))];
        const filteredTypes = allTypes.filter(t => typeIdsInFiliale.includes(t.idtype_personnel));
        setTypes(filteredTypes);
        setFilteredTypes(filteredTypes);
      } else {
        setTypes(allTypes);
        setFilteredTypes(allTypes);
      }
    } catch (error) {
      console.error('Erreur récupération types:', error);
      setTypes([]);
      setFilteredTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = types.filter(type =>
        type.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.idtype_personnel?.toString().includes(searchTerm)
      );
      setFilteredTypes(filtered);
    } else {
      setFilteredTypes(types);
    }
    setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
  }, [searchTerm, types]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTypes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Performance par Type de Personnel"
        subtitle="Sélectionnez un type de personnel pour gérer les performances"
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un type de personnel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Types de Personnel</h3>
            <p className="text-sm text-gray-500 mt-1">Cliquez sur un type pour accéder aux performances des employés</p>
            <p className="text-xs text-gray-500 mt-2">
              {filteredTypes.length} type(s) de personnel trouvé(s)
            </p>
          </div>

          {filteredTypes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {searchTerm ? 'Aucun type de personnel trouvé' : 'Aucun type de personnel trouvé'}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {currentItems.map((type) => (
                  <div
                    key={type.idtype_personnel}
                    onClick={() => navigate(`/performance/${type.idtype_personnel}`)}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{type.libelle}</h4>
                        <p className="text-sm text-gray-500">ID: {type.idtype_personnel}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Page {currentPage} sur {totalPages} ({filteredTypes.length} éléments)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceTypes;
